import React, { createContext, useReducer, useMemo, useContext, useRef } from "react";

type AuthUser = {
  id: string;
  email?: string;
  username?: string;
  role?: string;
  roles?: string[];
  name?: string;
  requirePasswordChange?: boolean;
  [key: string]: unknown;
};

type State = {
  status: "unknown" | "authenticated" | "unauthenticated";
  user?: AuthUser;
};

type Action =
  | { type: "AUTH_OK"; user: any }
  | { type: "AUTH_NONE" }
  | { type: "RESET" };

const AuthContext = createContext<{ state: State; dispatch: React.Dispatch<Action> } | null>(null);

function reducer(state: State, action: Action): State {
  let newState: State;

  switch (action.type) {
    case "AUTH_OK":
      // Check if user data actually changed to avoid unnecessary rerenders
      if (state.status === "authenticated" && state.user) {
        const userChanged = JSON.stringify(state.user) !== JSON.stringify(action.user);
        if (!userChanged) {
          // User data hasn't changed, return existing state to prevent rerender
          return state;
        }
      }
      console.log("[AUTH] transition", state.status, "→ authenticated", action.user?.email || action.user?.id);
      newState = { status: "authenticated", user: action.user };
      break;
    case "AUTH_NONE":
      // Only transition if not already unauthenticated
      if (state.status === "unauthenticated") {
        return state;
      }
      console.log("[AUTH] transition", state.status, "→ unauthenticated");
      newState = { status: "unauthenticated" };
      break;
    case "RESET":
      // Only reset if not already unknown
      if (state.status === "unknown") {
        return state;
      }
      console.log("[AUTH] transition", state.status, "→ unknown");
      newState = { status: "unknown" };
      break;
    default:
      return state;
  }

  // Update global immediately in reducer
  if (typeof window !== 'undefined') {
    (window as any).__APP = {
      ...(window as any).__APP,
      authStatus: newState.status,
      authUser: newState.user ?? null
    };
    console.log("[AUTH] Global updated immediately:", newState.status, newState.user?.email || newState.user?.id || 'null');
  }

  return newState;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { status: "unknown" });

  // Track previous state to avoid unnecessary effects
  const prevStateRef = useRef(state);

  const value = useMemo(() => ({ state, dispatch }), [state]);

  // Only log and update global when state actually changes
  React.useEffect(() => {
    const prevState = prevStateRef.current;
    const statusChanged = prevState.status !== state.status;
    const userChanged = JSON.stringify(prevState.user) !== JSON.stringify(state.user);

    if (statusChanged || userChanged) {
      console.debug("[AUTH] Provider state", state.status, state.user);
      (window as any).__APP = {
        ...(window as any).__APP,
        authStatus: state.status,
        authUser: state.user ?? null
      };
      prevStateRef.current = state;
    }
  }, [state]);

  // CAPACITOR REMOVED: SplashScreen code - mobile app is now billposter-expo

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Legacy exports for compatibility
export const useAuthStore = useAuth;
export type User = State['user'];
export type AuthStatus = State['status'];
